class OrderQuestionPartValidator {
    /**
     * Constructs an instance of OrderQuestionPartValidator.
     * @param {Object} $injector - The AngularJS injector service.
     * @param {Object} model - The model to be validated.
     */
    constructor($injector, model) {
        this.$injector = $injector;
        this.model = model;

        this._TaskDataService = null;
        this._translate = null;
        this._fetchContext = null;
        this._messageTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.LOGIC.ORDER-QUESTION-PART';
        this._message = null;
        this._moreInfoTranslateString = 'CMS.FACTORIES.MODELS.VALIDATORS.LOGIC.ORDER-QUESTION-PART-MORE-INFO';
        this._forced_check = false;
    }

    /**
     * Lazily initializes and returns the TaskDataService.
     * @returns {Object} The TaskDataService instance.
     */
    getTaskDataService() {
        if (!this._TaskDataService) {
            this._TaskDataService = this.$injector.get('TaskDataService');
        }
        return this._TaskDataService;
    }

    /**
     * Lazily initializes and returns the $translate service.
     * @returns {Object} The $translate service instance.
     */
    getTranslateService() {
        if (!this._translate) {
            this._translate = this.$injector.get('$translate');
        }
        return this._translate;
    }

    /**
     * Lazily initializes and returns the fetchContext.
     * @returns {Object} The fetchContext instance.
     */
    getFetchContext() {
        if (!this._fetchContext) {
            this._fetchContext = this.getTaskDataService().fetchContext();
        }
        return this._fetchContext;
    }

    /**
     * Validates the order of question parts.
     * @param {Object} from - The source object initiating the validation.
     * @returns {Object} The validation result.
     */
    validate(from) {
        let result = {};
        let error = false;

        let parents = this.model.getParents();
        let sheet = _.find(parents, item => item.getType() === 'sheet');

        if (sheet && typeof sheet.getLayout === 'function') {
            let layout = sheet.getLayout();
            if (typeof layout.getRows === 'function') {
                let rows = layout.getRows();
                let container_list = [];

                let question_parts = sheet.getQuestionParts();
                let question_parts_index = {};
                for (let key in question_parts) {
                    let question_part = question_parts[key];
                    question_parts_index[question_part.getUUID()] = parseInt(key) + 1;
                }

                for (let row_key in rows) {
                    let row = rows[row_key];
                    let max_loops = 100;
                    let counter = 0;
                    while (counter < max_loops) {
                        let col_left = row.cols[0];
                        let col_right = row.cols[1];

                        if (!col_left) break;

                        if (col_left.list[counter]) {
                            let left_container_uuid = col_left.list[counter].content;
                            let left_container_object = this.getFetchContext().read(left_container_uuid);

                            if (left_container_object.data.questionPart) {
                                let left_index = question_parts_index[left_container_object.data.questionPart];
                                container_list.push({
                                    'index': left_index,
                                    'questionPart': this.getFetchContext().read(left_container_object.data.questionPart)
                                });
                            }
                        }

                        if (col_right && col_right.list[counter]) {
                            let right_container_uuid = col_right.list[counter].content;
                            let right_container_object = this.getFetchContext().read(right_container_uuid);

                            if (right_container_object.data.questionPart) {
                                let right_index = question_parts_index[right_container_object.data.questionPart];
                                container_list.push({
                                    'index': right_index,
                                    'questionPart': this.getFetchContext().read(right_container_object.data.questionPart)
                                });
                            }
                        }

                        counter++;
                    }
                }

                for (let key in container_list) {
                    let container = container_list[key];

                    if (container.questionPart.getUUID() !== this.model.getUUID()) continue;

                    let container_prev = container_list[parseInt(key) - 1];
                    let container_next = container_list[parseInt(key) + 1];

                    if (container_prev) {
                        let diff = container.index - container_prev.index;
                        if (diff !== 0 && diff !== 1) {
                            error = true;
                            break;
                        }
                    }

                    if (container_next) {
                        let diff = container_next.index - container.index;
                        if (diff !== 0 && diff !== 1) {
                            error = true;
                            break;
                        }
                    }
                }
            }
        }

        if (container_list) {
            let questionPartOrderString = [];
            let lastQuestionPartUUID;
            for (let key in container_list) {
                let container = container_list[key];
                if (!lastQuestionPartUUID || lastQuestionPartUUID !== container.questionPart.getUUID()) {
                    questionPartOrderString.push(container.questionPart.getQuestionHeadline());
                    lastQuestionPartUUID = container.questionPart.getUUID();
                }
            }

            result = {
                'onSheetBlur': false,
                'onlyBanner': false,
                'validator': this.getValidatorName(),
                'modelUUID': this.model.getUUID(),
                'error': error,
                'status': error ? 'DANGER' : 'SUCCESS', // WARNING | DANGER | SUCCESS
                'message': this.getErrorMessage(container_list),
                'message_template': false,
                'more_info': questionPartOrderString ? this.getTranslateService().instant(this._moreInfoTranslateString, { s: ('<br>' + questionPartOrderString.join(', ')) }) : '',
                'callbacks': [
                    'validationDisplayContainerHeadline-qp-' + this.model.getUUID()
                ]
            };
        }

        return result;
    }

    /**
     * Generates the error message for the validation.
     * @param {Array} container_list - The list of containers.
     * @returns {string} The error message.
     */
    getErrorMessage(container_list) {
        let questionHeadline = this.model.getQuestionHeadline();

        this._message = this.getTranslateService().instant(this._messageTranslateString, {
            s: questionHeadline ? questionHeadline : ''
        });

        return this._message;
    }

    /**
     * Returns the forced check status.
     * @returns {boolean} The forced check status.
     */
    forcedCheck() {
        return this._forced_check;
    }

    /**
     * Returns the name of the validator.
     * @returns {string} The validator name.
     */
    getValidatorName() {
        return 'OrderQuestionPartValidator';
    }
}

export default OrderQuestionPartValidator;